// Tells the server this user has a Sentinel ticket open, and reads back who else is on which ticket.

import { useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { currentUserAtom } from '@/hooks/use-auth';
import {
  sendTicketPresenceAtom,
  ticketViewersAtom,
  watchTicketPresenceAtom,
  websocketStatusAtom,
} from '@/stores/websocket.store';
import type { TicketViewer } from '@/types/websocket.types';

// The server drops a viewer after 30s of silence, so two beats can go missing before it shows.
const BEAT_MS = 10_000;
const TYPING_IDLE_MS = 5_000;
// Matches the server's 30s: asking again is what clears someone who vanished from a ticket
// nobody else has open, and mends anything a dropped event left wrong.
const WATCH_REFRESH_MS = 30_000;
// A tab left in the background this long no longer counts as viewing. Browsers slow a hidden
// tab's timers to one a minute after about five, and beats that far apart made the person
// blink in and out of everyone's banner. Long enough to look something up in another tab.
const HIDDEN_GRACE_MS = 120_000;
const NOBODY: TicketViewer[] = [];

export interface TicketPresence {
  /** Other people on the open ticket. */
  viewers: TicketViewer[];
  /** Other people on every ticket that has any, by ticket id. */
  viewersByTicket: Record<number, TicketViewer[]>;
  markTyping: () => void;
}

export function useTicketPresence(ticketId: number | null): TicketPresence {
  const status = useAtomValue(websocketStatusAtom);
  const announced = useAtomValue(ticketViewersAtom);
  const me = useAtomValue(currentUserAtom);
  const send = useSetAtom(sendTicketPresenceAtom);
  const watch = useSetAtom(watchTicketPresenceAtom);
  // Kept with its ticket, so a half-written reply on one ticket never reads as typing on the next.
  const [typingOn, setTypingOn] = useState<number | null>(null);
  const typing = ticketId !== null && typingOn === ticketId;
  const idleTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [away, setAway] = useState(false);
  useEffect(() => {
    let grace: ReturnType<typeof setTimeout> | undefined;
    const onVisibility = () => {
      clearTimeout(grace);
      if (document.visibilityState === 'visible') setAway(false);
      else grace = setTimeout(() => setAway(true), HIDDEN_GRACE_MS);
    };
    onVisibility();
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      clearTimeout(grace);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);
  const online = status === 'authenticated' && !away;
  const openTicket = away ? null : ticketId;

  // Runs again when typing flips, which is what sends the change at once.
  useEffect(() => {
    if (openTicket === null || !online) return;
    send({ ticketId: openTicket, typing });
    const beat = setInterval(() => send({ ticketId: openTicket, typing }), BEAT_MS);
    return () => clearInterval(beat);
  }, [openTicket, typing, online, send]);

  useEffect(() => {
    if (openTicket === null) return;
    return () => send(null);
  }, [openTicket, send]);

  // Coming back from the background starts this again, which fetches a fresh snapshot.
  useEffect(() => {
    if (!online) return;
    watch(true);
    const refresh = setInterval(() => watch(true), WATCH_REFRESH_MS);
    return () => {
      clearInterval(refresh);
      watch(false);
    };
  }, [online, watch]);

  useEffect(
    () => () => {
      if (idleTimer.current) clearTimeout(idleTimer.current);
    },
    [],
  );

  const markTyping = useCallback(() => {
    setTypingOn(ticketId);
    if (idleTimer.current) clearTimeout(idleTimer.current);
    idleTimer.current = setTimeout(() => setTypingOn(null), TYPING_IDLE_MS);
  }, [ticketId]);

  const myId = String(me?.id);
  // One result per announced array: a ticket that did not change hands its row the same array.
  const tidied = useRef({ myId, results: new WeakMap<TicketViewer[], TicketViewer[]>() });
  const viewersByTicket = useMemo(() => {
    if (tidied.current.myId !== myId) tidied.current = { myId, results: new WeakMap() };
    const { results } = tidied.current;
    const others: Record<number, TicketViewer[]> = {};
    for (const [id, everyone] of Object.entries(announced)) {
      if (!results.has(everyone)) {
        // Typists first, then by name: only the first few faces are shown, and they must be the
        // people the banner names. The server's order is not stable between updates.
        results.set(
          everyone,
          everyone
            .filter((viewer) => viewer.userId !== myId)
            .sort(
              (a, b) =>
                Number(b.typing) - Number(a.typing) || a.name.localeCompare(b.name) || a.userId.localeCompare(b.userId),
            ),
        );
      }
      const rest = results.get(everyone)!;
      if (rest.length > 0) others[Number(id)] = rest;
    }
    return others;
  }, [announced, myId]);

  const viewers = (ticketId !== null && viewersByTicket[ticketId]) || NOBODY;

  return { viewers, viewersByTicket, markTyping };
}
